import { useState, useRef, type DragEvent } from 'react'
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react'
import { productImageUrl } from '../lib/image-url'

interface Props {
  label: string
  value: string
  onChange: (newUrl: string) => void
  hint?: string
}

const MAX_SIZE_MB = 10
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif'

/**
 * Dropzone para una sola imagen de producto.
 *
 * - Drag & drop o click para abrir selector.
 * - Sube a `/api/admin/uploads/product-image` (multipart/form-data, campo "file").
 * - Cuando responde con `{ url }`, llama `onChange(url)` para que el form
 *   guarde la ruta (ej. `/uploads/productos/lh3k-abc123.jpg`).
 * - Si el campo ya tiene un valor (incluso legacy como `item-1.png`), muestra
 *   el preview con `productImageUrl()` y permite reemplazar o eliminar.
 */
export function ImageDropzone({ label, value, onChange, hint }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  async function uploadFile(file: File) {
    setError(null)
    if (!file.type.startsWith('image/')) {
      setError('El archivo no es una imagen')
      return
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Archivo muy grande (máx ${MAX_SIZE_MB}MB)`)
      return
    }
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch('/api/admin/uploads/product-image', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      })
      const data = (await r.json().catch(() => ({}))) as { url?: string; error?: string }
      if (!r.ok || !data.url) {
        throw new Error(data.error || `HTTP ${r.status}`)
      }
      onChange(data.url)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setUploading(false)
    }
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) uploadFile(file)
    e.target.value = ''
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) uploadFile(file)
  }

  function onRemove() {
    onChange('')
    setError(null)
  }

  const hasImage = !!value

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-ink-700">{label}</label>
        {hint && <span className="text-[10px] text-ink-500">{hint}</span>}
      </div>

      {hasImage ? (
        <div className="relative">
          <img
            src={productImageUrl(value)}
            alt={label}
            referrerPolicy="no-referrer"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).src = '/img/sin-imagen.jpg'
            }}
            className="aspect-square w-full rounded-lg border border-ink-200 bg-ink-50 object-contain"
          />
          <button
            type="button"
            onClick={onRemove}
            disabled={uploading}
            className="absolute right-1.5 top-1.5 grid size-7 place-items-center rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 disabled:opacity-50"
            title="Eliminar imagen"
          >
            <X className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="absolute bottom-1.5 left-1.5 right-1.5 inline-flex items-center justify-center gap-1.5 rounded-md bg-ink-900/80 px-2 py-1 text-xs font-medium text-white hover:bg-ink-900 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="size-3 animate-spin" /> : <Upload className="size-3" />}
            {uploading ? 'Subiendo…' : 'Reemplazar'}
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`grid aspect-square cursor-pointer place-items-center rounded-lg border-2 border-dashed text-center transition-colors ${
            dragOver
              ? 'border-brand-500 bg-brand-50'
              : 'border-ink-200 bg-ink-50 hover:border-brand-400 hover:bg-brand-50/30'
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-ink-600">
              <Loader2 className="size-6 animate-spin" />
              <span className="text-xs font-medium">Subiendo…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5 px-3 text-ink-500">
              <ImageIcon className="size-7 text-ink-400" />
              <span className="text-xs font-medium text-ink-700">Arrastra o haz click</span>
              <span className="text-[10px] text-ink-500">JPG · PNG · WebP — máx {MAX_SIZE_MB}MB</span>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={onPick}
        className="hidden"
      />

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] text-red-700">
          {error}
        </div>
      )}
    </div>
  )
}
